import { type AnyFieldApi, useForm } from '@tanstack/react-form';
import { Settings as SettingsIcon } from 'lucide-react';
import { toast } from 'sonner';
import { z } from 'zod';

import {
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Input,
  Label,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@cold-ambulance/shadcn';
import {
  FieldMessage,
  FormMessage,
  errorToFormIssue,
  useAction,
  useData,
} from '@cold-ambulance/ui';

import { PageHeader } from '../../components/page-header.tsx';

type FormValues = {
  platform: {
    companyName: string;
    bankName: string;
    bankAccountName: string;
    bankIban: string;
  };
  email: {
    fromEmail: string;
    fromName: string;
    replyTo: string;
  };
};

const DEFAULTS: FormValues = {
  platform: {
    companyName: '',
    bankName: '',
    bankAccountName: '',
    bankIban: '',
  },
  email: {
    fromEmail: '',
    fromName: '',
    replyTo: '',
  },
};

const schema = z.object({
  platform: z.object({
    companyName: z.string().trim().min(1, 'Required'),
    bankName: z.string(),
    bankAccountName: z.string(),
    bankIban: z.string(),
  }),
  email: z.object({
    fromEmail: z
      .string()
      .min(1, 'Required')
      .email({ message: 'Invalid email' }),
    fromName: z.string().trim().min(1, 'Required'),
    replyTo: z
      .string()
      .min(1, 'Required')
      .email({ message: 'Invalid email' }),
  }),
});

function valuesFromRecords(
  records: { section: string; key: string; value: string }[],
): FormValues {
  const values: FormValues = structuredClone(DEFAULTS);
  for (const { section, key, value } of records) {
    if (section === 'platform' && key in values.platform) {
      (values.platform as Record<string, string>)[key] = value;
    } else if (section === 'email' && key in values.email) {
      (values.email as Record<string, string>)[key] = value;
    }
  }
  return values;
}

function flatten(
  values: FormValues,
): { section: string; key: string; value: string }[] {
  return Object.entries(values).flatMap(([section, fields]) =>
    Object.entries(fields as Record<string, string>).map(([key, value]) => ({
      section,
      key,
      value,
    })),
  );
}

function TextField({
  field,
  label,
  type,
  wide,
  hint,
}: {
  field: AnyFieldApi;
  label: string;
  type?: 'text' | 'email';
  wide?: boolean;
  hint?: string;
}) {
  const containerClass = wide ? 'space-y-2 md:col-span-2' : 'space-y-2';
  return (
    <div className={containerClass}>
      <Label htmlFor={field.name}>{label}</Label>
      <Input
        id={field.name}
        type={type}
        value={field.state.value ?? ''}
        onBlur={field.handleBlur}
        onChange={(e) => field.handleChange(e.target.value)}
      />
      {hint ? (
        <p className="text-muted-foreground text-xs">{hint}</p>
      ) : null}
      <FieldMessage errors={field.state.meta.errors} />
    </div>
  );
}

export default function SettingsPage() {
  return (
    <div className="w-full max-w-4xl">
      <PageHeader
        title="Settings"
        description="Platform data and email sender configuration"
        icon={SettingsIcon}
      />
      <SettingsBody />
    </div>
  );
}

function SettingsBody() {
  const query = useData('GET /settings', { isActive: true });

  if (query.isLoading || !query.data) {
    return <div className="text-muted-foreground py-8">Loading...</div>;
  }

  return <SettingsForm initial={valuesFromRecords(query.data.records)} />;
}

function SettingsForm({ initial }: { initial: FormValues }) {
  const updateAction = useAction('PATCH /settings/bulk', {
    invalidate: ['GET /settings'],
  });

  const form = useForm({
    defaultValues: initial,
    validators: {
      onSubmit: schema,
      onSubmitAsync: async ({ value }) => {
        try {
          await updateAction.mutateAsync({ settings: flatten(value) });
          toast.success('Settings saved');
          return void 0;
        } catch (error) {
          return errorToFormIssue(error);
        }
      },
    },
  });

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        e.stopPropagation();
        form.handleSubmit();
      }}
    >
      <Tabs defaultValue="platform" className="w-full">
        <TabsList>
          <TabsTrigger value="platform">Platform</TabsTrigger>
          <TabsTrigger value="email">Email</TabsTrigger>
        </TabsList>

        <TabsContent value="platform" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Company</CardTitle>
            </CardHeader>
            <CardContent className="grid gap-4 md:grid-cols-2">
              <form.Field name="platform.companyName">
                {(field) => (
                  <TextField field={field} label="Company name" wide />
                )}
              </form.Field>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Bank account</CardTitle>
            </CardHeader>
            <CardContent className="grid gap-4 md:grid-cols-2">
              <form.Field name="platform.bankName">
                {(field) => <TextField field={field} label="Bank name" />}
              </form.Field>
              <form.Field name="platform.bankAccountName">
                {(field) => <TextField field={field} label="Account name" />}
              </form.Field>
              <form.Field name="platform.bankIban">
                {(field) => <TextField field={field} label="IBAN" wide />}
              </form.Field>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="email" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Email sender</CardTitle>
            </CardHeader>
            <CardContent className="grid gap-4 md:grid-cols-2">
              <form.Field name="email.fromEmail">
                {(field) => (
                  <TextField field={field} label="From email" type="email" />
                )}
              </form.Field>
              <form.Field name="email.fromName">
                {(field) => <TextField field={field} label="From name" />}
              </form.Field>
              <form.Field name="email.replyTo">
                {(field) => (
                  <TextField
                    field={field}
                    label="Reply-to"
                    type="email"
                    wide
                  />
                )}
              </form.Field>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      <div className="mt-6 flex items-center justify-end gap-3">
        <form.Subscribe
          selector={(state) => state.errors}
          children={(errors) => <FormMessage errors={errors} />}
        />
        <form.Subscribe
          selector={(state) => [state.canSubmit, state.isSubmitting]}
          children={([canSubmit, isSubmitting]) => (
            <Button type="submit" disabled={!canSubmit || isSubmitting}>
              {isSubmitting ? 'Saving...' : 'Save changes'}
            </Button>
          )}
        />
      </div>
    </form>
  );
}
